'use client'

import { useEffect, useState } from 'react'

import { getAddressByCoord } from '@/entities/place'
import {
  getGeolocation,
  GEOLOCATION_ERROR_TYPES,
  type IGeolocationError,
} from '@/shared/external/geolocation'

import { CAR_STORY_NEAREST_PLACE_DEFAULT_LOCATION } from '../lib/nearestPlace'

const DEFAULT_REGION_LABEL = '성남시 분당구'

const REGION_SUFFIX_REGEX = /(특별자치시|특별자치도|특별시|광역시|도)$/
const CITY_DISTRICT_REGEX = /\S+시\s+\S+구$/

/**
 * 카카오 region 응답을 "시+구" 라벨로 가공한다.
 * - region_2가 이미 "성남시 분당구" 형태면 그대로 사용
 * - 그 외엔 region_1의 "특별시/광역시/도" 접미사를 떼고 region_2와 결합
 */
const formatRegionLabel = (region1: string, region2: string): string => {
  if (CITY_DISTRICT_REGEX.test(region2)) {
    return region2
  }
  const cleanedRegion1 = region1.replace(REGION_SUFFIX_REGEX, '')
  return `${cleanedRegion1} ${region2}`.trim()
}

interface ICoords {
  latitude: number
  longitude: number
}

interface IUseNearestPlaceLocationParams {
  /** 권한 거부 시 호출 (웹은 안내 다이얼로그, 앱은 native 처리) */
  onPermissionDenied: () => void
}

/**
 * 주변 정비소 섹션의 위치 권한 + 좌표 + 지역 라벨 상태 일체를 관리하는 훅.
 *
 * 책임:
 * - mount 시 permissions API로 위치 권한 자동 확인 (granted면 자동 fetch)
 * - 사용자 클릭 시 좌표 획득 + 지역 라벨 갱신 + active state 보정
 */
const useNearestPlaceLocation = ({
  onPermissionDenied,
}: IUseNearestPlaceLocationParams) => {
  const [coords, setCoords] = useState<ICoords>({
    latitude: CAR_STORY_NEAREST_PLACE_DEFAULT_LOCATION.LATITUDE,
    longitude: CAR_STORY_NEAREST_PLACE_DEFAULT_LOCATION.LONGITUDE,
  })
  const [regionLabel, setRegionLabel] = useState(DEFAULT_REGION_LABEL)
  const [isRequestingLocation, setIsRequestingLocation] = useState(false)

  const applyRegionLabel = async (target: ICoords) => {
    try {
      const { documents } = await getAddressByCoord(target)
      const address = documents[0]?.address
      if (address) {
        setRegionLabel(
          formatRegionLabel(
            address.region_1depth_name,
            address.region_2depth_name
          )
        )
      }
    } catch {
      // 지역 라벨 조회 실패는 좌표만 사용해서 진행 — UX 영향 미미
    }
  }

  // 진입 시 위치 권한이 'granted'인 경우에만 자동으로 좌표 + 지역 라벨 fetch.
  // 인앱 WebView의 'prompt'는 native 허용/미허용 모두에서 반환될 수 있어 자동 호출 시
  // 미허용 케이스에서 페이지 진입마다 native 팝업이 노출된다.
  // 'prompt'/'denied'에서는 자동 호출하지 않고 '내 위치' 버튼 클릭 시에만 권한 요청을 트리거한다.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then(async status => {
        if (status.state !== 'granted') return

        try {
          const geolocation = await getGeolocation()
          const next: ICoords = {
            latitude: geolocation.coords.latitude,
            longitude: geolocation.coords.longitude,
          }
          setCoords(next)
          await applyRegionLabel(next)
        } catch {
          // 자동 fetch 실패는 사용자 클릭으로 재시도 가능하므로 무시
        }
      })
      .catch(() => {
        // Permissions API 미지원 환경 → 기존 동작 유지
      })
  }, [])

  const requestLocation = async () => {
    // 진행 중에 다시 호출돼도 무시 (중복 클릭 race 차단)
    if (isRequestingLocation) return
    setIsRequestingLocation(true)

    try {
      // 1) 위치 권한 + 좌표 획득 (실패해도 step 2는 skip되도록 분리)
      let nextCoords: ICoords | null = null
      try {
        const geolocation = await getGeolocation()
        nextCoords = {
          latitude: geolocation.coords.latitude,
          longitude: geolocation.coords.longitude,
        }
        setCoords(nextCoords)
      } catch (error) {
        const geolocationError = error as IGeolocationError
        if (
          geolocationError.type === GEOLOCATION_ERROR_TYPES.PERMISSION_DENIED
        ) {
          onPermissionDenied()
        }
      }

      // 2) 좌표로 지역 라벨 갱신 (권한 거부 등 좌표가 없으면 skip)
      if (nextCoords) {
        await applyRegionLabel(nextCoords)
      }
    } finally {
      setIsRequestingLocation(false)
    }
  }

  return {
    coords,
    regionLabel,
    isRequestingLocation,
    requestLocation,
  }
}

export default useNearestPlaceLocation
